import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Guid } from 'guid-typescript';
import { lastValueFrom, Observable } from 'rxjs';
import { SiaErrorDialog } from '../dialogs/sia-error-dialog/sia-error-dialog';
import { SiaCacheDatabaseService } from './cache-database.service';

/** Status codes that should be suppressed (not shown as errors) */
const SUPPRESSED_STATUS_CODES = [499];
/** Status codes that should not trigger the error dialog */
const NO_DIALOG_STATUS_CODES = [400, 401, 499];

@Injectable({
  providedIn: 'root',
})
export class SiaHttpService {
  #cacheDatabaseService = inject(SiaCacheDatabaseService);
  #httpClient = inject(HttpClient);
  #dialog = inject(MatDialog);

  #apiUrl?: string | null;
  #activeRequests = new Map<string, AbortController>();

  /**
   * Create an abort controller for a request
   * @param requestId Unique identifier for the request
   * @returns AbortController instance
   */
  createAbortController(requestId: string): AbortController {
    // Cancel any existing request with the same ID
    this.cancelRequest(requestId);

    const controller = new AbortController();
    this.#activeRequests.set(requestId, controller);
    return controller;
  }

  /**
   * Cancel a specific request by its ID (client-side abort only)
   * @param requestId The ID of the request to cancel
   */
  cancelRequest(requestId: string): void;
  /**
   * Cancel a specific request by calling the controller's cancel endpoint
   * This ensures the server stops processing the request
   * @param endpoint The controller cancel endpoint (e.g., 'chat-ai/cancel', 'database-ai/cancel')
   * @param requestId The ID of the request to cancel
   */
  cancelRequest(endpoint: string, requestId: string): Promise<void>;
  async cancelRequest(endpointOrRequestId: string, requestId?: string) {
    // If only one parameter, it's the old behavior (client-side abort only)
    if (requestId === undefined) {
      const controller = this.#activeRequests.get(endpointOrRequestId);
      if (controller) {
        controller.abort();
        this.#activeRequests.delete(endpointOrRequestId);
      }
      return;
    }

    // Abort client-side immediately to prevent the browser from receiving the 499 response
    const controller = this.#activeRequests.get(requestId);
    if (controller) {
      controller.abort();
      this.#activeRequests.delete(requestId);
    }

    // Then notify the server to stop processing
    try {
      await this.post(`${endpointOrRequestId}/${requestId}`, {});
    } catch (err) {
      // Don't throw - cancellation failure shouldn't break the flow
    }
  }

  /**
   * Cancel all active requests
   */
  cancelAllRequests(): void {
    this.#activeRequests.forEach((controller) => controller.abort());
    this.#activeRequests.clear();
  }

  /**
   * Setup abort controller options if requestId is provided
   */
  #setupAbortController(options: any, requestId?: string): any {
    if (!requestId) return options;
    const controller = this.createAbortController(requestId);
    return {
      ...options,
      context: new HttpContext(),
      signal: controller.signal,
    };
  }

  /**
   * Handle HTTP errors with common logic for all methods
   */
  #handleError(e: any, url: string, requestId?: string): any {
    // Clean up the request from active requests map
    if (requestId) {
      this.#activeRequests.delete(requestId);
    }

    const status = e.status || e.error?.status;

    // Suppress certain errors (e.g., cancelled requests) - don't log or throw
    if (SUPPRESSED_STATUS_CODES.includes(status)) {
      return undefined;
    }

    // Show error dialog unless status is in the no-dialog list
    if (e.error?.title && !NO_DIALOG_STATUS_CODES.includes(status)) {
      this.showError(url, e.error);
    }

    throw e;
  }

  /**
   * Cleanup request from active requests map
   */
  #cleanupRequest(requestId?: string): void {
    if (requestId) {
      this.#activeRequests.delete(requestId);
    }
  }

  /**
   * Execute an HTTP request with common error handling and cleanup
   */
  #executeRequest<T>(request$: Observable<T>, url: string, requestId?: string): Promise<T> {
    return lastValueFrom(request$, { defaultValue: undefined })
      .catch((e: any) => this.#handleError(e, url, requestId))
      .finally(() => this.#cleanupRequest(requestId));
  }

  get(endpoint: string, options?: any, geoArea?: string, requestId?: string): Promise<any> {
    if (endpoint.includes('?')) {
      console.error('Endpoint should not contain query parameters');
    }
    const apiUrl = this.getApiUrl(geoArea);
    const guid = Guid.create().toString();
    const department = window.localStorage.getItem('department');
    const url = department
      ? `${apiUrl}/${endpoint}?department=${department}&gd=${guid}`
      : `${apiUrl}/${endpoint}?gd=${guid}`;

    options = this.#setupAbortController(options, requestId);

    return this.#executeRequest(this.#httpClient.get(url, options), url, requestId);
  }

  getCache(endpoint: string, options: any, preKey: string, geoArea?: string): Promise<any> {
    const apiUrl = this.getApiUrl(geoArea);
    const url = `${apiUrl}/${endpoint}`;
    const key = this.#cacheDatabaseService.createResponseKey(preKey, url, options);
    return this.#cacheDatabaseService.getResponse(key).then((data: any) => {
      if (!data) {
        return this.get(endpoint, options, geoArea).then((data) => {
          if (data) {
            return this.#cacheDatabaseService.setResponse(key, data).then((_: any) => {
              return data;
            });
          } else {
            return data;
          }
        });
      } else {
        return data;
      }
    });
  }

  getUsingPostCache(endpoint: string, params: any, preKey: string, geoArea?: string): Promise<any> {
    const apiUrl = this.getApiUrl(geoArea);
    const url = `${apiUrl}/${endpoint}`;
    const key = this.#cacheDatabaseService.createResponseKey(preKey, url, params);

    return this.#cacheDatabaseService.getResponse(key).then((data: any) => {
      if (!data) {
        return this.post(endpoint, params, {}).then((data) => {
          if (data) {
            return this.#cacheDatabaseService.setResponse(key, data).then((_: any) => {
              return data;
            });
          } else {
            return data;
          }
        });
      } else {
        return data;
      }
    });
  }

  post(endpoint: string, data: any, options?: any, geoArea?: string, requestId?: string): Promise<any> {
    const apiUrl = this.getApiUrl(geoArea);
    const url = `${apiUrl}/${endpoint}`;
    const department = window.localStorage.getItem('department');
    if (!Array.isArray(data)) {
      data = department ? { ...data, department } : data;
    }

    options = this.#setupAbortController(options, requestId);

    return this.#executeRequest(this.#httpClient.post(url, data, options), url, requestId);
  }

  showError(url: string, error: any) {
    this.#dialog.open(SiaErrorDialog, {
      data: { message: error.detail, title: error.title, requestUrl: url, type: error.type },
    });
  }

  postCache(cacheKeys: string, endpoint: string, data: any, options?: any, geoArea?: string): Promise<any> {
    return this.post(endpoint, data, options, geoArea).then((returnedData) => {
      this.#cacheDatabaseService.clearAllResponsesContaining(cacheKeys).then((_: any) => {
        return returnedData;
      });
    });
  }

  put(endpoint: string, data: any, options?: any, geoArea?: string, requestId?: string): Promise<any> {
    const apiUrl = this.getApiUrl(geoArea);
    const url = `${apiUrl}/${endpoint}`;

    options = this.#setupAbortController(options, requestId);

    return this.#executeRequest(this.#httpClient.put(url, data, options), url, requestId);
  }

  putCache(endpoint: string, data: any, cacheKeys: string, options: any, geoArea: string): Promise<any> {
    return this.put(endpoint, data, options, geoArea).then((returnedData) => {
      return this.#cacheDatabaseService.clearAllResponsesContaining(cacheKeys).then((_: any) => {
        return returnedData;
      });
    });
  }

  deleteCache(endpoint: string, cacheKeys: string, options: any, geoArea: string): Promise<any> {
    return this.delete(endpoint, options, geoArea).then((data) => {
      this.#cacheDatabaseService.clearAllResponsesContaining(cacheKeys).then((_: any) => {
        return data;
      });
    });
  }

  deleteOnlyCache(cacheKeys: string) {
    return this.#cacheDatabaseService.clearAllResponsesContaining(cacheKeys);
  }

  delete(endpoint: string, options?: any, geoArea?: string, requestId?: string): Promise<any> {
    const apiUrl = this.getApiUrl(geoArea);
    const url = `${apiUrl}/${endpoint}`;

    options = this.#setupAbortController(options, requestId);

    return this.#executeRequest(this.#httpClient.delete(url, options), url, requestId);
  }

  execDownload(controller: any, params: any, geoArea?: any) {
    var options = {
      responseType: 'blob',
      params: params,
      geoArea,
    };
    return this.get(controller, options, '');
  }

  getApiUrl(geoArea?: string): string | null | undefined {
    if (!this.#apiUrl) {
      var url: string | undefined = '';
      var apiUrls = window.sessionStorage.getItem('APIURLS');
      if (!apiUrls) {
        location.reload();
        this.#apiUrl = null;
        return this.#apiUrl;
      }
      var urls: { id: string; value: string }[] = JSON.parse(apiUrls);
      if (!urls || urls.length == 0) {
        this.#apiUrl = null;
        return this.#apiUrl;
      }
      if (!geoArea) {
        url = urls.find((x) => x.id == 'LOCALURL')?.value;
      } else {
        url = urls.find((x) => x.id == 'LOCALURL' + geoArea)?.value;
      }
      this.#apiUrl = url;
    }
    return this.#apiUrl;
  }
}
